import TitleBar from '../../../components/TitleBar';
import React from 'react';
import { AntModal, Button, Loading } from '../../../components';
import { useParams, useSearchParams } from 'react-router-dom';
import { Footer } from '../../../components/common/Footer';
import { FindAllStrategiesByLocation } from './components/StrategySummaryField';
import { useMutation, useQuery } from '@apollo/client';
import { FETCH_STRATEGY_SUMMARY, UPDATE_STRATEGY } from '../queries';
import { lastUpdateDateFormatter } from '../../../utils';
import { FinalizeStrategy } from './forms/FinalizeStrategy';
import StrategyStatus from './components/StrategyStatus';
import { SummaryContent } from './components';
import ExposureSummaryTable from './strategyReview/ExposureSummaryTable';
import StrategyComponents from './strategyReview/StrategyComponents';
import QueryErrorBoundary from '../../../components/error-handling';
import { useUserContext } from '../../../context/PermissionsContext';
import TitleUpdater from '../../../components/common/TitleUpdater';

interface DataReturned {
  findAllStrategiesByLocation: FindAllStrategiesByLocation[];
}

export default function StrategySummary() {
  const params = useParams();
  const strategyId = params.strategyId!;
  const locationId = params.locationId!;
  let [searchParams] = useSearchParams();
  const locationName = searchParams.get('locationName')!;
  const strategyLength = searchParams.get('strategyLength')!;
  const [modalOpen, setModalOpen] = React.useState(false);

  const { canApprove } = useUserContext();
  const { data, loading, error } = useQuery(FETCH_STRATEGY_SUMMARY, {
    variables: { uuid: locationId },
  });

  const [UpdateStrategySummary] = useMutation(UPDATE_STRATEGY, {
    refetchQueries: ['FetchStrategySummary'],
  });

  const currentStrategy = data?.findAllStrategiesByLocation.find(
    (strategy: any) => strategy.uuid === strategyId,
  );

  return (
    <>
      <TitleUpdater title={`${strategyLength} Month Strategy`} />
      <TitleBar
        title="BCP Hub"
        subtitle={`${locationName} // ${strategyLength} Month Strategy`}
      />
      <section className="grid gap-6">
        <QueryErrorBoundary error={error}>
          {!loading ? (
            <>
              <StrategyStatus
                status={currentStrategy?.bcpStatus}
                lastUpdate={currentStrategy?.lastUpdate}
              />
              <SummaryContent
                title="Strategy Commentary"
                data={currentStrategy}
                loading={loading}
                error={error}
                updateGQL={UpdateStrategySummary}
              />
              <section className="grid lg:grid-cols-2 gap-6">
                <ExposureSummaryTable data={currentStrategy} />
                <StrategyComponents
                  strategyID={currentStrategy?.uuid}
                  locationName={locationName}
                  replacementTime={strategyLength}
                  strategyType="location"
                />
              </section>
              {canApprove && (
                <section className="w-full flex justify-end mb-4">
                  <Button
                    type="primary"
                    className="w-[250px] text-sm"
                    onClick={() => setModalOpen(true)}
                  >
                    Finalize Strategy
                  </Button>
                </section>
              )}
            </>
          ) : (
            <Loading />
          )}
        </QueryErrorBoundary>
      </section>
      <Footer backPath={`/strategies/location/${locationId}`} />
      <AntModal
        title="Finalize Strategy"
        openModal={modalOpen}
        onCancel={() => setModalOpen(false)}
      >
        <FinalizeStrategy
          lastUpdate={
            currentStrategy?.bcpStatus.toUpperCase() === 'FINALIZED'
              ? lastUpdateDateFormatter(currentStrategy?.lastUpdate)
              : 'NEVER'
          }
          strategyId={strategyId}
          onCancel={() => setModalOpen(false)}
        />
      </AntModal>
    </>
  );
}
